import { useEffect, useState } from "react";
import api, { eur } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { openPdfBlob } from "@/lib/pdf";

const eurN = (v) => (v == null ? "—" : eur(v));
const pct = (v) => (v == null || v === "" ? "—" : `${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);
const qty = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 }));

function ProductTable({ title, rows, kind, testid }) {
  if (!rows || rows.length === 0) return null;
  const tot = rows.reduce((a, r) => a + (r.prezzo_totale || 0), 0);
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-primary">{title}</span>
        <Badge variant="secondary" className="text-[10px]">{rows.length}</Badge>
        <span className="ml-auto text-xs font-semibold">{eur(tot)}</span>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/60">
            <tr className="text-left">
              <th className="px-2 py-1.5 font-semibold">Codice</th>
              <th className="px-2 py-1.5 font-semibold">Nome</th>
              {kind !== "servizi" && <th className="px-2 py-1.5 font-semibold">Marca</th>}
              <th className="px-2 py-1.5 text-right font-semibold">Q.tà</th>
              {kind === "articoli" && <th className="px-2 py-1.5 font-semibold">UM</th>}
              <th className="px-2 py-1.5 text-right font-semibold">Prezzo unit.</th>
              {kind === "moduli" && <th className="px-2 py-1.5 text-right font-semibold">€/W</th>}
              {kind === "moduli" && <th className="px-2 py-1.5 text-right font-semibold">kWp</th>}
              <th className="px-2 py-1.5 text-right font-semibold">Sconto %</th>
              <th className="px-2 py-1.5 text-right font-semibold">Totale</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border" data-testid={`${testid}-row`}>
                <td className="px-2 py-1 font-mono text-[11px] text-muted-foreground">{r.codice || "—"}</td>
                <td className="px-2 py-1">{r.nome}</td>
                {kind !== "servizi" && <td className="px-2 py-1 text-muted-foreground">{r.marca || "—"}</td>}
                <td className="px-2 py-1 text-right font-mono">{qty(r.quantita)}</td>
                {kind === "articoli" && <td className="px-2 py-1 text-muted-foreground">{r.um || "—"}</td>}
                <td className="px-2 py-1 text-right font-mono">{eurN(r.prezzo_unitario)}</td>
                {kind === "moduli" && <td className="px-2 py-1 text-right font-mono">{eurN(r.prezzo_watt)}</td>}
                {kind === "moduli" && <td className="px-2 py-1 text-right font-mono">{qty(r.kwp)}</td>}
                <td className="px-2 py-1 text-right font-mono">{r.sconto_pct ? pct(r.sconto_pct) : "—"}</td>
                <td className="px-2 py-1 text-right font-mono font-semibold">{eurN(r.prezzo_totale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Info = ({ label, value, testid }) => (
  <div><div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div><div className="text-sm font-semibold" data-testid={testid}>{value ?? "—"}</div></div>
);

function adaptFier(det) {
  return {
    numero: det.numero_preventivo,
    origine: "Fier",
    tipologia: det.tipologia,
    stato: det.stato,
    modalita_pagamento: det.modalita_pagamento,
    aliquota_iva: det.aliquota_iva,
    importo_con_iva_eur: det.importo_con_iva_eur,
    margine_commessa_pct: det.margine_commessa_pct,
    margine_target_pct: det.margine_target_pct,
    provvigione_pct: det.provvigione_pct,
    sconto_cliente_pct: det.sconto_cliente_pct,
    indice_contribuzione: det.indice_contribuzione,
    moduli: det.moduli,
    articoli: det.articoli,
    servizi: det.servizi,
    has_pdf: det.has_pdf,
  };
}

const _rx = (s, re) => re.test(String(s || ""));
const _SERV = /serviz|opzion|manodoper|installaz|montagg|\bposa\b|trasporto|noleggio|sopralluogo/i;
const _MOD = /modul/i;
const _ACC = /accumul/i;

function adaptCrm(det) {
  const righe = det.righe || [];
  const isAccumulo = (r) => _rx(r.tipologia, _ACC) || _rx(r.descrizione, _ACC);
  const isModulo = (r) => !isAccumulo(r) && (_rx(r.tipologia, _MOD) || (Number(r.kwp) || 0) > 0);
  const isServizio = (r) => _rx(r.tipologia, _SERV) || _rx(r.descrizione, _SERV);
  const toRow = (r) => {
    const kwpUnit = Number(r.kwp) || 0;
    const q = Number(r.quantita) || 0;
    return {
      codice: r.codice,
      nome: r.descrizione,
      marca: r.marca || null,
      quantita: r.quantita,
      um: r.um,
      prezzo_unitario: r.prezzo_unitario,
      prezzo_watt: kwpUnit > 0 ? Number(r.prezzo_unitario || 0) / (kwpUnit * 1000) : null,
      kwp: kwpUnit > 0 ? Number((kwpUnit * q).toFixed(3)) : null,
      sconto_pct: r.sconto_pct,
      prezzo_totale: r.prezzo_totale,
    };
  };
  const moduli = [], articoli = [], servizi = [];
  righe.forEach((r) => {
    if (isModulo(r)) moduli.push(toRow(r));
    else if (isServizio(r)) servizi.push(toRow(r));
    else articoli.push(toRow(r));
  });
  return {
    numero: det.numero,
    origine: "CRM",
    tipologia: det.tetto_config ? "Configuratore" : "Preventivatore",
    stato: det.stato,
    modalita_pagamento: det.modalita_pagamento,
    aliquota_iva: det.iva_pct,
    importo_con_iva_eur: det.totale,
    margine_commessa_pct: det.margine_pct,
    margine_target_pct: det.margine_target_pct,
    provvigione_pct: det.provvigione_pct,
    sconto_cliente_pct: det.sconto_globale_pct,
    indice_contribuzione: det.indice_contribuzione,
    moduli, articoli, servizi,
    has_pdf: det.stato && det.stato !== "Bozza",
    fier_numero_ordine: det.fier_numero_ordine,
    scostamento: !!((det.link_ordine || {}).scostamento),
  };
}

export function RichQuoteDetail({ source, id, canMargin, hasPdf }) {
  const isCrm = source === "crm";
  const [det, setDet] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setDet(null); setErr(false);
    const url = isCrm ? `/crm-quotes/${id}` : `/quotes/${id}`;
    api.get(url)
      .then((r) => { if (alive) setDet(isCrm ? adaptCrm(r.data) : adaptFier(r.data)); })
      .catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [source, id, isCrm]);

  const openPdf = async () => {
    try {
      const url = isCrm ? `/crm-quotes/${id}/pdf` : `/quotes/${id}/pdf`;
      const r = await api.get(url, { responseType: "blob" });
      openPdfBlob(r.data, `preventivo_${id}.pdf`);
    } catch { toast.error("PDF non disponibile per questo preventivo"); }
  };

  if (err) return <div className="p-4 text-sm text-muted-foreground">Dettaglio non disponibile.</div>;
  if (!det) return <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico dettaglio…</div>;

  const showPdf = hasPdf != null ? hasPdf : det.has_pdf;

  return (
    <div className="space-y-4 p-4" data-testid={`rich-quote-detail-${id}`}>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4 lg:grid-cols-7">
        <Info label="N° Preventivo" value={det.numero} testid="qd-numero" />
        <Info label="Origine" value={det.origine} testid="qd-origine" />
        <Info label="Tipologia" value={det.tipologia} testid="qd-tipologia" />
        <Info label="Stato" value={det.stato} testid="qd-stato" />
        <Info label="Modalità pagamento" value={det.modalita_pagamento} testid="qd-pagamento" />
        <Info label="Aliquota IVA" value={det.aliquota_iva != null ? `${det.aliquota_iva}%` : "—"} testid="qd-iva" />
        <Info label="Importo con IVA" value={det.importo_con_iva_eur != null ? eur(det.importo_con_iva_eur) : "—"} testid="qd-imponibile" />
        {det.fier_numero_ordine && <Info label="Ordine Fier" value={det.fier_numero_ordine} testid="qd-fier-ordine" />}
      </div>

      {det.scostamento && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive" data-testid="qd-scostamento">
          Attenzione: scostamento tra questo preventivo e l'ordine Fier collegato (importi o kWp diversi). Fanno fede i dati dell'ordine Fier.
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border border-primary/30 bg-primary/5 p-3 sm:grid-cols-5" data-testid="qd-margine-box">
        {canMargin && <Info label="Margine commessa" value={pct(det.margine_commessa_pct)} testid="qd-margine" />}
        {canMargin && <Info label="Margine target" value={pct(det.margine_target_pct)} testid="qd-margine-target" />}
        {canMargin && <Info label="Provvigione" value={pct(det.provvigione_pct)} testid="qd-provvigione" />}
        {canMargin && <Info label="Sconto cliente" value={pct(det.sconto_cliente_pct)} testid="qd-sconto" />}
        <Info label="Indice di contribuzione" value={det.indice_contribuzione ?? "—"} testid="qd-indice" />
      </div>

      <ProductTable title="Moduli" kind="moduli" rows={det.moduli} testid="qd-tab-moduli" />
      <ProductTable title="Articoli" kind="articoli" rows={det.articoli} testid="qd-tab-articoli" />
      <ProductTable title="Servizi e Opzioni" kind="servizi" rows={det.servizi} testid="qd-tab-servizi" />

      {showPdf ? (
        <Button size="sm" variant="outline" onClick={openPdf} data-testid={`quote-pdf-btn-${id}`}>
          <FileDown className="mr-2 h-4 w-4" /> Scarica PDF preventivo
        </Button>
      ) : (
        <div className="text-xs text-muted-foreground">Nessun PDF disponibile per questo preventivo.</div>
      )}
    </div>
  );
}
